import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const COLORS = ["#2A5CE0", "#3BB6D9", "#e08a2a", "#8b5cf6"];

function FigureTable({ fig, testid }) {
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            {fig.columns.map((c, i) => (
              <th key={i} className="text-left px-3 py-2 font-semibold border-b-2" style={{ borderColor: "var(--border)" }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fig.rows.map((r, i) => (
            <tr key={i} style={{ background: i % 2 ? "var(--muted)" : "transparent" }}>
              {r.map((cell, j) => <td key={j} className="px-3 py-1.5 font-mono text-xs">{String(cell)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function toRows(series) {
  const xs = [];
  series.forEach((s) => s.points.forEach(([x]) => { if (!xs.includes(x)) xs.push(x); }));
  return xs.map((x) => {
    const row = { x };
    series.forEach((s) => { const p = s.points.find(([px]) => px === x); if (p) row[s.name] = p[1]; });
    return row;
  });
}

function FigureChart({ fig, testid }) {
  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
      <XAxis dataKey="x" type={fig.type === "scatter" ? "number" : "category"} tick={{ fontSize: 11 }}
        label={{ value: fig.x_label, position: "insideBottom", offset: -4, fontSize: 11 }} />
      <YAxis tick={{ fontSize: 11 }} label={{ value: fig.y_label, angle: -90, position: "insideLeft", fontSize: 11 }} />
      <Tooltip />
      {fig.series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
    </>
  );
  let chart;
  if (fig.type === "scatter") {
    chart = (
      <ScatterChart margin={{ top: 8, right: 16, bottom: 16, left: 8 }}>
        {axes}
        {fig.series.map((s, i) => (
          <Scatter key={s.name} name={s.name} data={s.points.map(([x, y]) => ({ x, y }))} dataKey="y" fill={COLORS[i % 4]} />
        ))}
      </ScatterChart>
    );
  } else {
    const rows = toRows(fig.series);
    const Chart = fig.type === "bar" ? BarChart : LineChart;
    chart = (
      <Chart data={rows} margin={{ top: 8, right: 16, bottom: 16, left: 8 }}>
        {axes}
        {fig.series.map((s, i) => fig.type === "bar"
          ? <Bar key={s.name} dataKey={s.name} fill={COLORS[i % 4]} radius={[4, 4, 0, 0]} />
          : <Line key={s.name} dataKey={s.name} stroke={COLORS[i % 4]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
      </Chart>
    );
  }
  return (
    <div className="h-64 w-full" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">{chart}</ResponsiveContainer>
    </div>
  );
}

export default function FigureView({ figures, idPrefix = "figure" }) {
  if (!figures || figures.length === 0) return null;
  return (
    <div className="space-y-4 my-5">
      {figures.map((fig, i) => (
        <figure key={i} className="rounded-xl p-4" style={{ background: "var(--paper)", border: "1px solid var(--border)" }}>
          {fig.title && <figcaption className="text-xs font-semibold mb-3" style={{ color: "var(--text-secondary)" }}>{fig.title}</figcaption>}
          {fig.type === "table"
            ? <FigureTable fig={fig} testid={`${idPrefix}-${i}`} />
            : <FigureChart fig={fig} testid={`${idPrefix}-${i}`} />}
        </figure>
      ))}
    </div>
  );
}
